import { MemberRole } from '../types/organisation';

/**
 * Check if a member has a specific role
 */
export const hasRole = (roles: MemberRole[] | undefined, targetRole: MemberRole): boolean => {
  return roles?.includes(targetRole) ?? false;
};

/**
 * Check if a member has any of the specified roles
 */
export const hasAnyRole = (roles: MemberRole[] | undefined, targetRoles: MemberRole[]): boolean => {
  return roles?.some(role => targetRoles.includes(role)) ?? false;
};

/**
 * Get the primary role (highest priority role) for display purposes
 * Priority order: superAdmin > admin > projectAdmin > organisationAdmin > moderator > facilitator > member
 */
export const getPrimaryRole = (roles: MemberRole[] | undefined): MemberRole => {
  if (!roles || roles.length === 0) return 'member';
  
  const rolePriority: Record<MemberRole, number> = {
    'superAdmin': 7,
    'admin': 6,
    'projectAdmin': 5,
    'organisationAdmin': 4,
    'moderator': 3,
    'steward': 3,
    'facilitator': 2,
    'wanderWriter': 2,
    'member': 1
  };
  
  return roles.reduce((highestRole, currentRole) => {
    return rolePriority[currentRole] > rolePriority[highestRole] ? currentRole : highestRole;
  }, roles[0]);
};

/**
 * Get display text for roles (for UI components)
 */
export const getRolesDisplayText = (roles: MemberRole[] | undefined): string => {
  if (!roles || roles.length === 0) return 'member';
  if (roles.length === 1) return roles[0];
  
  // For multiple roles, show primary role + count
  const primaryRole = getPrimaryRole(roles);
  if (roles.length === 2) {
    return roles.join(', ');
  }
  return `${primaryRole} +${roles.length - 1}`;
}; 